import { useEffect, useRef, useState } from "react";
import { TextField, ThemeProvider, createTheme } from "@mui/material";
import { validateNewFileSelection } from "../../../utils/fileUploadUtils";
import IconAlertIcon from "../../../assets/svgs/icon-alert.svg";
import Notification from "../../fluxo-tax/Notification/notification";

interface FormBasicInputProps {
  label: any;
  title: string;
  setValue: any;
  value: any;
  icon?: any;
  displayEdit?: any;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  pnSimilarSelect?: any;
  width?: string | number;
  className?: string;
  maxLength?: number;
  setSelectedFiles?: any;
  selectedFiles?: any;
  focusTrigger?: string | number;
}

const FormBasicInputImage = ({
  title,
  label,
  setValue,
  value,
  className,
  width = "40ch",
  maxLength,
  inputProps,
  setSelectedFiles,
  selectedFiles,
  focusTrigger,
}: FormBasicInputProps) => {
  const customTheme = createTheme({
    palette: { primary: { main: "#000000" } },
  });

  const textFieldRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // Regex para remover a maioria dos emojis e símbolos de apresentação // Usamos o intervalo Unicode mais abrangente com a flag 'u'

  useEffect(() => {
    if (textFieldRef.current) {
      textFieldRef.current.focus();
    }
  }, [focusTrigger]);

  const removeEmojis = (text: string) => {
    const extendedPictographic = /\p{Extended_Pictographic}/gu;
    const zwj = /\u200D/gu;
    const vs16 = /\uFE0F/gu;
    const basicSymbols = /[\u2600-\u27BF]/gu;

    let sanitizedText = text;

    sanitizedText = sanitizedText.replaceAll(extendedPictographic, "");
    sanitizedText = sanitizedText.replaceAll(zwj, "");
    sanitizedText = sanitizedText.replaceAll(vs16, "");
    sanitizedText = sanitizedText.replaceAll(basicSymbols, "");

    return sanitizedText;
  };

  const constMaxImgSize = 5 * 1024 * 1024;
  const maxFiles = 5;
  const acceptedImageTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf",
  ]; 

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = removeEmojis(event.target.value);
    setValue(sanitizedValue);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files); 

    const { errorMessage: validationError, validFiles } =
      validateNewFileSelection(
        droppedFiles,
        selectedFiles,
        maxFiles,
        constMaxImgSize,
        acceptedImageTypes,
      );

    if (validationError) {
      setErrorMessage(validationError);
      setTimeout(() => setErrorMessage(null), 8000);
      return;
    }

    setSelectedFiles((prev: File[]) => [...prev, ...validFiles]);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    // 1. Lógica para arquivos colados (mantida)
    const pastedFiles = Array.from(event.clipboardData.files);

    if (pastedFiles.length > 0) {
      const { errorMessage: validationError, validFiles } =
        validateNewFileSelection(
          pastedFiles,
          selectedFiles,
          maxFiles,
          constMaxImgSize,
          acceptedImageTypes,
        );

      if (validationError) {
        setErrorMessage(validationError);
        setTimeout(() => setErrorMessage(null), 8000);
        return;
      }

      setSelectedFiles((prev: File[]) => [...prev, ...validFiles]);
    } // 🎯 CORREÇÃO AQUI: Lógica para texto colado (incluindo emojis) // Isso garante que, se o usuário colar apenas texto com emojis (e não arquivos), // o emoji seja removido imediatamente, mesmo que o onChange ainda não tenha sido acionado.

    const pastedText = event.clipboardData.getData("text");
    if (pastedText) {
      const sanitizedText = removeEmojis(pastedText); // Previne o comportamento padrão de colar o texto com emojis
      event.preventDefault(); // Junta o valor atual com o texto higienizado // Se o maxLength estiver sendo usado pelo inputProps, o browser ainda fará o corte, // mas é mais seguro aplicar a lógica de corte aqui se o campo estiver quase cheio.
      const newValue = value + sanitizedText;
      setValue(newValue.substring(0, maxLength));
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  return (
    <ThemeProvider theme={customTheme}>
      <>
        <h4>{title}</h4>
        <p className="lineLabel lineLabelSmallInput">{label}</p>
        <section
          aria-labelledby="upload-title"
          data-testid="drop-zone"
          className={className}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          style={{
            width: width,
            minHeight: "100px",
            maxHeight: "300px",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "rgba(0, 0, 0, 0.06)",
            padding: "10px",
            overflowY: "auto",
            overflowX: "hidden",
            boxSizing: "border-box",
            marginTop: 15,
            borderBottom: "2px solid",
          }}
        >
          <TextField
            inputRef={textFieldRef}
            value={value} // 🎯 onChange AGORA CHAMA handleInputChange (que sanitiza)
            onChange={handleInputChange}
            inputProps={{ maxLength, ...inputProps }}
            onPaste={handlePaste}
            multiline
            rows={4}
            variant="filled"
            focused
            sx={{
              // ... (Estilos mantidos)
              "& .MuiFilledInput-root": {
                borderRadius: "0px",
                backgroundColor: "rgba(0, 0, 0, 0.06)",
                fontSize: "17px",
                overflowY: "auto",
              },
              "& .MuiInputBase-input": {
                marginTop: "-22px",
                height: "100px",
                overflowY: "auto",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                scrollbarWidth: "thin",
                scrollbarColor: "#ccc transparent",
                paddingRight: "10px",
                "&::-webkit-scrollbar": {
                  width: "5px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "8px",
                },
              },
            }}
          />
        </section>
        {errorMessage && (
          <Notification
            label={errorMessage}
            backgroundError="#FFD9D9"
            widthError="385px"
            img={IconAlertIcon}
            setNotification={setErrorMessage}
          />
        )}
      </>
    </ThemeProvider>
  );
};

export default FormBasicInputImage;
